import { useRef } from 'react'
import { useInView, useReducedMotion } from 'motion/react'
import { BadgeCheck, FolderOpen, KeyRound, LayoutGrid, Lock, ShieldCheck, type LucideIcon } from 'lucide-react'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { platformBadges, type PlatformBadge, type PlatformBadgeKind } from '@/data/about'

const ICONS: Partial<Record<PlatformBadgeKind, LucideIcon>> = {
  suite: LayoutGrid,
  files: FolderOpen,
  security: ShieldCheck,
  health: Lock,
  privacy: BadgeCheck,
  identity: KeyRound,
}

function Badge({ badge }: { badge: PlatformBadge }) {
  const Icon = ICONS[badge.kind]
  const app =
    badge.kind === 'word' || badge.kind === 'excel' || badge.kind === 'powerpoint' || badge.kind === 'outlook'
      ? badge.kind
      : null
  return (
    <span className="flex shrink-0 items-center gap-3 text-[var(--color-landing-gray-900)] dark:text-white">
      {app ? (
        <OfficeAppIcon app={app} className="h-9 w-9" />
      ) : Icon ? (
        <Icon aria-hidden className="h-7 w-7" strokeWidth={1.75} />
      ) : null}
      <span className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-semibold tracking-[-0.01em] md:text-2xl">
        {badge.label}
      </span>
    </span>
  )
}

/**
 * The reference's logo strip: a faded label in a quarter column and a 20s
 * marquee of the apps Templatean runs in and the compliance it claims, with
 * faded edges. Pauses off-screen; a wrapped static list under reduced motion.
 */
export function BadgeStrip({ label }: { label: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref)
  const reduce = useReducedMotion()

  return (
    <section className="landing-section-sm">
      <div className="landing-container grid items-center gap-6 lg:grid-cols-[minmax(0,0.25fr)_minmax(0,1fr)] lg:gap-12">
        <p className="landing-text-body-lg max-w-[24ch] text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
          {label}
        </p>

        {reduce ? (
          <ul className="flex flex-wrap gap-x-10 gap-y-5">
            {platformBadges.map((badge) => (
              <li key={badge.label}>
                <Badge badge={badge} />
              </li>
            ))}
          </ul>
        ) : (
          <div
            ref={ref}
            className="overflow-hidden py-2 [mask-image:linear-gradient(to_right,transparent,#000_12%,#000_88%,transparent)]"
          >
            <ul className="sr-only">
              {platformBadges.map((badge) => (
                <li key={badge.label}>{badge.label}</li>
              ))}
            </ul>
            <div
              aria-hidden
              className="animate-marquee flex w-max"
              style={{ animationDuration: '20s', animationPlayState: inView ? 'running' : 'paused' }}
            >
              {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0 items-center gap-14 pr-14">
                  {platformBadges.map((badge) => (
                    <Badge key={`${copy}-${badge.label}`} badge={badge} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
